import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Filter,
  Search,
  Package
} from 'lucide-react';
import moment from 'moment';
import { sessionsAPI, studentsAPI, studentSubscriptionsAPI } from '../services/api';
import { useApp } from '../contexts/AppContext';
import { useTheme } from '../contexts/ThemeContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const StudentSessionsPage = () => {
  const { studentId, subscriptionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganizationId } = useApp();
  const { colors } = useTheme();

  // Get organization ID from state or context
  const organizationId = location.state?.organizationId || selectedOrganizationId || 'all';

  const [student, setStudent] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    // Always fetch data when component mounts or IDs change
    if (studentId && subscriptionId) {
      fetchAllData();
    }
  }, [studentId, subscriptionId, organizationId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching data for:', { organizationId, studentId, subscriptionId });

      // Fetch student, subscription, and sessions in parallel
      const [studentRes, subscriptionRes, sessionsRes] = await Promise.all([
        studentsAPI.getById(organizationId, studentId),
        studentSubscriptionsAPI.getById(organizationId, subscriptionId),
        sessionsAPI.getBySubscriptionId(organizationId, subscriptionId)
      ]);

      console.log('API responses:', { studentRes, subscriptionRes, sessionsRes });

      setStudent(studentRes.data.data || studentRes.data);
      setSubscription(subscriptionRes.data.data || subscriptionRes.data);
      setSessions(sessionsRes.data.data || sessionsRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load session data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      no_show: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      rescheduled: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'no_show': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredAndSortedSessions = sessions
    .filter(session => {
      // If no search term, match all; otherwise check notes, trainer, location
      const matchesSearch = !searchTerm ||
        session.sessionNotes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.trainerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.location?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || session.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'date':
          aValue = new Date(a.sessionDate);
          bValue = new Date(b.sessionDate);
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'trainer':
          aValue = a.trainerName || '';
          bValue = b.trainerName || '';
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Calculate accurate stats based on sessions list (same logic as StudentDetailsPage)
  const getSubscriptionStats = () => {
    if (!subscription) return { used: 0, remaining: 0, totalCapacity: 0 };

    // Canonical "used" = sessions that consumed a credit (isDeducted), matching the
    // backend ledger; fall back to the status heuristic for legacy rows lacking the
    // field (credit-lifecycle-root-cause.md I-1).
    const used = sessions.filter(s =>
      typeof s.isDeducted === 'boolean' ? s.isDeducted : s.status !== 'cancelled'
    ).length;

    // Total capacity = Base + Bonus
    const baseTotal = subscription.totalSessions || 0;
    const bonus = subscription.bonusSessions || 0;
    const totalCapacity = baseTotal + bonus;

    // Remaining
    const remaining = Math.max(0, totalCapacity - used);

    return { used, remaining, totalCapacity };
  };

  const stats = getSubscriptionStats();

  const handleSessionClick = (sessionId) => {
    navigate(`/sessions/${sessionId}`);
  };

  const normalizeStatus = (status) => {
    if (!status) return '';
    return status.toLowerCase().replace(/-/g, '_');
  };

  const getStatusLabel = (status) => {
    const s = normalizeStatus(status);
    switch (s) {
      case 'completed': return 'Tamamlandı';
      case 'scheduled': return 'Planlandı';
      case 'cancelled': return 'İptal';
      case 'no_show': return 'Gelmedi';
      default: return status;
    }
  };

  const getStatusStyles = (status) => {
    const s = normalizeStatus(status);
    switch (s) {
      case 'completed': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'scheduled': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'cancelled': return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'no_show': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      default: return 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !student || !subscription) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className={`text-lg font-medium ${colors.textPrimary} mb-2`}>Error Loading Sessions</h3>
        <p className={`${colors.textMuted} mb-4`}>{error || 'Session data not found'}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${colors.bgPrimary}`}>
      {/* Premium Floating Profile Card */}
      <div className="px-3 sm:px-6 pt-4 sm:pt-6">
        <div className={`${colors.bgTertiary} border ${colors.border} rounded-2xl relative overflow-hidden shadow-sm`}>
          {/* Subtle Background Decoration */}
          <div className="absolute top-0 right-0 w-64 h-full bg-blue-500/5 blur-[80px] -mr-32 rounded-full" />
          
          <div className="px-5 py-3.5 relative z-10">
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
              <div className="flex items-center space-x-4 min-w-0">
                <button
                  onClick={() => navigate(-1)}
                  className={`p-2 ${colors.bgHoverStrong} rounded-xl transition-all duration-200 border border-white/5 hover:border-white/10 shadow-sm shrink-0`}
                >
                  <ArrowLeft className={`w-4 h-4 ${colors.textPrimary}`} />
                </button>
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="relative shrink-0">
                    {student.profilePicture ? (
                      <img
                        src={student.profilePicture}
                        alt={student.name}
                        className="w-12 h-12 rounded-xl object-cover border-2 border-blue-500/20 shadow-xl shadow-black/20"
                      />
                    ) : (
                      <div className={`w-14 h-14 ${colors.bgHoverStrong} rounded-xl flex items-center justify-center border border-white/10 shadow-xl shadow-black/20`}>
                        <User className={`w-7 h-7 text-zinc-500`} />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <h1 className={`text-xl font-bold ${colors.textPrimary} tracking-tight truncate`}>
                        {student.name} {student.surname}
                      </h1>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Package className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                      <span className={`text-xs font-semibold ${colors.textSecondary} truncate`}>{subscription.packageName}</span>
                      <span className="text-zinc-600">•</span>
                      <span className={`text-xs font-medium text-zinc-500`}>{sessions.length} seans geçmişi</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-6 border-t lg:border-t-0 lg:border-l border-white/5 pt-3 lg:pt-0 lg:pl-6">
                <div className="flex flex-col items-end gap-1.5 min-w-[140px]">
                  <div className="flex items-center justify-between w-full">
                    <span className={`text-[10px] font-bold uppercase tracking-widest text-zinc-400`}>İlerleme</span>
                    <span className={`text-xs font-black ${colors.textPrimary}`}>{stats.used} / {stats.totalCapacity}</span>
                  </div>
                  <div className={`w-full h-1.5 ${colors.bgHoverStrong} rounded-full overflow-hidden border border-white/5`}>
                    <div 
                      className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.3)] transition-all duration-1000"
                      style={{ width: `${stats.totalCapacity > 0 ? Math.min(100, (stats.used / stats.totalCapacity) * 100) : 0}%` }}
                    />
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1 shrink-0">
                  <div className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-blue-500/20 bg-blue-500/5 text-blue-500`}>
                    {stats.remaining} Seans Kaldı
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-bold text-zinc-500 uppercase tracking-tighter`}>Son Güncelleme:</span>
                    <span className={`text-[10px] font-bold ${colors.textSecondary}`}>{moment().format('DD/MM/YYYY')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search - Compact Glassmorphism */}
      <div className="px-3 sm:px-6 mt-4">
        <div className={`${colors.bgTertiary} border ${colors.border} rounded-xl px-4 py-2 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 shadow-sm`}>
          <div className="relative flex-1 max-w-md">
            <Search className={`absolute left-3.5 top-1/2 transform -translate-y-1/2 text-zinc-500 w-3.5 h-3.5`} />
            <input
              type="text"
              placeholder="Not, eğitmen veya konuma göre ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-9 pr-4 py-1.5 w-full bg-transparent border-0 text-[13px] font-medium leading-none ${colors.textPrimary} focus:ring-0 placeholder-zinc-500`}
            />
          </div>

          <div className="flex items-center gap-2">
            <div className={`flex items-center px-3 py-1.5 rounded-lg ${colors.bgHoverStrong} border border-white/5`}>
              <Filter className={`w-3 h-3 text-zinc-500 mr-2`} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`bg-transparent border-0 p-0 text-[11px] font-bold uppercase tracking-tight ${colors.textSecondary} focus:ring-0 cursor-pointer`}
              >
                <option value="all">Tüm Durumlar</option>
                <option value="completed">Tamamlandı</option>
                <option value="scheduled">Planlandı</option>
                <option value="cancelled">İptal</option>
                <option value="no_show">Gelmedi</option>
              </select>
            </div>

            <div className={`flex items-center px-3 py-1.5 rounded-lg ${colors.bgHoverStrong} border border-white/5`}>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [sort, order] = e.target.value.split('-');
                  setSortBy(sort);
                  setSortOrder(order);
                }}
                className={`bg-transparent border-0 p-0 text-[11px] font-bold uppercase tracking-tight ${colors.textSecondary} focus:ring-0 cursor-pointer`}
              >
                <option value="date-desc">En Yeni</option>
                <option value="date-asc">En Eski</option>
                <option value="trainer-asc">Eğitmen A-Z</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      <div className="p-6">
        {filteredAndSortedSessions.length === 0 ? (
          <div className={`text-center py-12 ${colors.bgTertiary} rounded-2xl border ${colors.border}`}>
            <Calendar className={`w-12 h-12 ${colors.textMuted} mx-auto mb-4`} />
            <h3 className={`text-lg font-medium ${colors.textPrimary} mb-2`}>
              {searchTerm || statusFilter !== 'all' ? 'No matching sessions found' : 'No sessions yet'}
            </h3>
            <p className={colors.textMuted}>
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Sessions will appear here once they are scheduled'
              }
            </p>
          </div>
        ) : (
          <div className={`${colors.bgTertiary} rounded-2xl border ${colors.border} overflow-hidden shadow-sm`}>
            <div className="overflow-x-auto scrollbar-none">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className={`${colors.bgHoverStrong}/30 border-b ${colors.border}`}>
                    <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                      Seans Bilgisi
                    </th>
                    <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                      Tarih & Saat
                    </th>
                    <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                      Eğitmen & Konum
                    </th>
                    <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                      Durum
                    </th>
                    <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-zinc-500 text-right">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {filteredAndSortedSessions.map((session, index) => (
                    <tr
                      key={session.id || session.SK || index}
                      className={`hover:bg-white/[0.02] transition-colors duration-150 cursor-pointer group`}
                      onClick={() => handleSessionClick(session.id || session.SK)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ring-1 ring-inset ${
                            session.status === 'completed' ? 'bg-green-500/10 text-green-500 ring-green-500/20' : 'bg-blue-500/10 text-blue-500 ring-blue-500/20'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="ml-4">
                            <div className={`text-[13px] font-bold ${colors.textPrimary}`}>
                              Seans #{index + 1}
                            </div>
                            {session.sessionNotes ? (
                              <div className={`text-[11px] text-zinc-500 max-w-[200px] truncate mt-0.5`}>
                                {session.sessionNotes}
                              </div>
                            ) : (
                                <div className={`text-[11px] italic text-zinc-600 mt-0.5`}>Not eklenmemiş</div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <div className={`flex items-center text-[13px] font-bold ${colors.textPrimary}`}>
                            <Calendar className="w-3.5 h-3.5 text-blue-500 mr-2" />
                            {moment(session.startTime || session.sessionDate).format('DD MMMM YYYY')}
                          </div>
                          <div className="flex items-center text-[11px] font-medium text-zinc-500">
                            <Clock className="w-3 h-3 text-zinc-600 mr-2" />
                            {/* Time must come from startTime (full datetime); sessionDate is date-only -> always 00:00 */}
                            {moment(session.startTime).format('HH:mm')}
                            {session.duration && (
                              <span className="ml-1 text-zinc-600">({session.duration} dk)</span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <div className={`flex items-center text-[12px] font-bold ${colors.textPrimary}`}>
                            <User className="w-3.5 h-3.5 text-zinc-500 mr-2" />
                            {session.trainerName || 'Belirtilmemiş'}
                          </div>
                          <div className="flex items-center text-[11px] font-medium text-zinc-500">
                            <MapPin className="w-3 h-3 text-zinc-600 mr-2" />
                            {session.location || 'Stüdyo'}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-tight border ${getStatusStyles(session.status)}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current mr-1.5 opacity-50 shadow-[0_0_8px_currentColor]" />
                          {getStatusLabel(session.status)}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSessionClick(session.id || session.SK);
                          }}
                          className={`inline-flex items-center px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all duration-200 ${colors.bgHoverStrong} border border-white/5 ${colors.textPrimary} hover:text-blue-500 hover:border-blue-500/30 shadow-sm`}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1.5" />
                          Detaylar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default StudentSessionsPage;